import { ChevronRight, ChevronDown, Repeat, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const STATIONS = [
  { n: "01", name: "The Market Intel Agent", d: "Scans your market weekly: what competitors publish, which angles convert, what's being ignored." },
  { n: "02", name: "The Content Planner Agent", d: "Turns that intel into the month's calendar — topics, formats, angles, cadence, mapped per channel." },
  { n: "03", name: "The Scriptwriter Agent", d: "Writes hooks, scripts, captions, and copy in your voice, formatted for each platform." },
  { n: "04", name: "The Studio Agent", d: "Produces the statics, motion graphics, and edits." },
  { n: "05", name: "The Spokesperson Agent", d: "Turns approved scripts into on-camera AI avatar video." },
  { n: "06", name: "The Repurposing Agent", d: "Atomizes one asset into every format each channel needs — a long video becomes shorts, stills, quote cards, an email." },
  { n: "07", name: "The Publishing Agent", d: "Schedules and auto-publishes across every channel at the right times." },
  { n: "08", name: "The Performance Agent", d: "Reads what actually performed and rewrites next month's plan." },
];

export const BackOffice = () => (
  <section className="bg-[var(--charcoal)] py-28 text-white md:py-36" data-testid="growth-back-office">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="max-w-2xl">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--mint)]">
            The back office
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="mt-7 t-section text-white">The content engine, station by station.</h2>
        </Reveal>
        <Reveal delay={140}>
          <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">
            Eight agents on one production line — research to publishing to performance. You never
            operate any of it. Your named expert runs the line and clears everything before it ships.
          </p>
        </Reveal>
      </div>

      {/* Pipeline: vertical numbered list with down-connectors on mobile/tablet,
          a two-row horizontal line with right-connectors on desktop. */}
      <div className="mt-16 grid grid-cols-1 gap-y-11 lg:grid-cols-4 lg:gap-x-6">
        {STATIONS.map((s, i) => (
          <Reveal key={s.n} delay={(i % 4) * 70}>
            <div className="relative flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors duration-300 hover:border-[var(--mint)]/40">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--mint)]/40 text-sm font-semibold text-[var(--mint)]">
                {s.n}
              </span>
              <h3 className="mt-4 text-[15px] font-semibold tracking-[-0.01em] text-[var(--mint)]">
                {s.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-white/75">{s.d}</p>

              {/* connector to next station */}
              {i < STATIONS.length - 1 && (
                <>
                  <ChevronDown
                    className="absolute -bottom-9 left-1/2 -translate-x-1/2 text-[var(--mint)]/45 lg:hidden"
                    size={22}
                    aria-hidden="true"
                  />
                  {i !== 3 && (
                    <ChevronRight
                      className="absolute -right-[19px] top-1/2 hidden -translate-y-1/2 text-[var(--mint)]/45 lg:block"
                      size={22}
                      aria-hidden="true"
                    />
                  )}
                </>
              )}
            </div>
          </Reveal>
        ))}
      </div>

      {/* The loop — the selling point */}
      <Reveal delay={120}>
        <div className="mt-16 flex items-start gap-4 rounded-2xl border border-[var(--mint)]/25 bg-[var(--mint)]/[0.07] p-6 sm:items-center sm:p-7">
          <Repeat size={24} className="mt-0.5 shrink-0 text-[var(--mint)] sm:mt-0" aria-hidden="true" />
          <p className="text-base font-semibold leading-relaxed tracking-[-0.01em] text-white sm:text-lg">
            It's a loop, not a checklist. Performance feeds intel, intel feeds the plan. Month three
            is smarter than month one.
          </p>
        </div>
      </Reveal>

      <Reveal delay={180}>
        <p className="mt-6 flex items-center gap-2 text-sm text-white/60">
          <ShieldCheck size={16} className="shrink-0 text-[var(--mint)]" aria-hidden="true" />
          Every piece passes your named human before it ships.
        </p>
      </Reveal>
    </div>
  </section>
);
